import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";

import { Category, Classification, Product, Schema } from "../../../../api";
import { ACTIONS, ENV, SSMARCA } from "../../../../utils";
import { usePermissions } from "../../../../hooks";

import styles from "./Product.module.scss";

const LIMIT_OPTIONS = [10, 20, 50];

function getProductsResponse(result) {
  const response = result?.products || result?.response || result || {};

  if (Array.isArray(response.docs)) {
    return response;
  }

  if (Array.isArray(response)) {
    return {
      docs: response,
      totalDocs: response.length,
      totalPages: 1,
      page: 1,
      limit: response.length,
    };
  }

  return {
    docs: [],
    totalDocs: 0,
    totalPages: 1,
    page: 1,
    limit: 10,
  };
}

function getProductId(product) {
  return String(product?._id || product?.id || "");
}

function getEntityId(entity) {
  if (!entity) {
    return "";
  }

  if (typeof entity === "object") {
    return String(entity._id || entity.id || "");
  }

  return String(entity);
}

function getCategoriesResponse(result) {
  const response = result?.categories || result?.response || result || {};

  if (Array.isArray(response.docs)) {
    return response.docs;
  }

  if (Array.isArray(response)) {
    return response;
  }

  return [];
}

function getClassificationsResponse(result) {
  const response =
    result?.classifications || result?.response || result || {};

  if (Array.isArray(response.docs)) {
    return response.docs;
  }

  if (Array.isArray(response)) {
    return response;
  }

  return [];
}

function getSchemasResponse(result) {
  const response = result?.schemas || result?.response || result || {};

  if (Array.isArray(response.docs)) {
    return response.docs;
  }

  if (Array.isArray(response)) {
    return response;
  }

  return [];
}

function getCategoryOptionLabel(category) {
  const level = Math.max(Number(category?.level || 1) - 1, 0);
  const prefix = level > 0 ? "— ".repeat(level) : "";

  return `${prefix}${category?.name || ""}`;
}

async function fetchClassificationsBySchema(schemaId) {
  const schemaController = new Schema();
  const classificationController = new Classification();

  const schemasResult = await schemaController.getSchemas(1, 1, {
    search: schemaId,
  });

  const schema = getSchemasResponse(schemasResult).find(
    (item) => item?.id === schemaId
  );

  if (!schema) {
    return [];
  }

  const result = await classificationController.getClassifications(1, 100, {
    schema: getEntityId(schema),
  });

  return getClassificationsResponse(result);
}

function MultiSelectFilter({
  label,
  options,
  selectedValues,
  onChange,
  disabled,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    function handlePointerDown(event) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, []);

  function toggleValue(value) {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter((item) => item !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  }

  const selectedOption =
    selectedValues.length === 1
      ? options.find((option) => option.value === selectedValues[0])
      : null;

  const summary =
    selectedValues.length === 0
      ? "Todas"
      : selectedValues.length === 1
      ? selectedOption?.label || "Todas"
      : `${selectedValues.length} seleccionadas`;

  return (
    <div className={styles.MultiSelectFilter} ref={containerRef}>
      <span className={styles.MultiSelectLabel}>{label}</span>

      <button
        type="button"
        className={styles.MultiSelectButton}
        onClick={() => setIsOpen((open) => !open)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span>{summary}</span>
        <span aria-hidden="true">⌄</span>
      </button>

      {isOpen && (
        <div className={styles.MultiSelectPanel} role="listbox">
          {options.length === 0 ? (
            <span className={styles.MultiSelectEmpty}>Sin opciones</span>
          ) : (
            options.map((option) => (
              <label key={option.value} className={styles.MultiSelectOption}>
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option.value)}
                  onChange={() => toggleValue(option.value)}
                />
                {option.label}
              </label>
            ))
          )}

          {selectedValues.length > 0 && (
            <button
              type="button"
              className={styles.MultiSelectClear}
              onClick={() => onChange([])}
            >
              Limpiar
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function getImageUrl(imagePath) {
  if (!imagePath) {
    return "";
  }

  if (
    String(imagePath).startsWith("http://") ||
    String(imagePath).startsWith("https://")
  ) {
    return imagePath;
  }

  const normalizedPath = String(imagePath)
    .replace(/\\/g, "/")
    .replace(/^\/+/, "");

  return `${ENV.SERVER_HOST}/${normalizedPath}`;
}

function getProductImage(product) {
  const images = Array.isArray(product?.images) ? product.images : [];

  const coverImage =
    images.find((image) => image?.isCover) || images[0];

  return getImageUrl(coverImage?.path);
}

function getCategoryName(category) {
  if (!category) {
    return "Sin categoría";
  }

  if (typeof category === "object") {
    return category.name || "Sin categoría";
  }

  return "Sin categoría";
}

function getBrandName(product) {
  if (!product?.brand || typeof product.brand !== "object") {
    return "";
  }

  return product.brand.name || "";
}

function getMinVariantPrice(product) {
  const variants = Array.isArray(product?.variants)
    ? product.variants.filter((variant) => variant.enabled !== false)
    : [];

  if (variants.length === 0) {
    return 0;
  }

  return Math.min(
    ...variants.map((variant) => Number(variant.price) || 0)
  );
}

function getMinOfferPrice(product) {
  const variants = Array.isArray(product?.variants)
    ? product.variants.filter(
        (variant) =>
          variant.enabled !== false &&
          variant.offerPrice !== null &&
          variant.offerPrice !== undefined &&
          Number(variant.offerPrice) >= 0
      )
    : [];

  if (variants.length === 0) {
    return null;
  }

  return Math.min(
    ...variants.map((variant) => Number(variant.offerPrice) || 0)
  );
}

function getMainSku(product) {
  const variants = Array.isArray(product?.variants)
    ? product.variants
    : [];

  if (variants.length === 0) {
    return "—";
  }

  return variants[0]?.sku || "—";
}

function formatCurrency(value) {
  const amount = Number(value) || 0;

  return `Q ${amount.toLocaleString("es-GT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function getStockStatus(product) {
  const stock = Number(product?.stock) || 0;
  const lowStockAlert = Number(product?.lowStockAlert) || 0;

  if (stock <= 0) {
    return {
      label: "Sin stock",
      className: styles.OutOfStockBadge,
    };
  }

  if (lowStockAlert > 0 && stock <= lowStockAlert) {
    return {
      label: "Bajo stock",
      className: styles.LowStockBadge,
    };
  }

  return {
    label: "Disponible",
    className: styles.AvailableStockBadge,
  };
}

function getProductPayload(product, visible) {
  return {
    id: product.id,
    name: product.name,
    slug: product.slug,
    category:
      typeof product.category === "object"
        ? product.category?._id || null
        : product.category || null,
    brand:
      typeof product.brand === "object"
        ? product.brand?._id || null
        : product.brand || null,
    description: product.description || "",
    variants: Array.isArray(product.variants)
      ? product.variants.map((variant) => ({
          sku: variant.sku,
          attributes: Array.isArray(variant.attributes)
            ? variant.attributes.map((attribute) => ({
                schema:
                  typeof attribute.schema === "object"
                    ? attribute.schema?._id || null
                    : attribute.schema || null,
                classification:
                  typeof attribute.classification === "object"
                    ? attribute.classification?._id || null
                    : attribute.classification || null,
                value: attribute.value || "",
              }))
            : [],
          price: Number(variant.price) || 0,
          offerPrice:
            variant.offerPrice === null ||
            variant.offerPrice === undefined ||
            variant.offerPrice === ""
              ? null
              : Number(variant.offerPrice),
          cost:
            variant.cost === null ||
            variant.cost === undefined ||
            variant.cost === ""
              ? null
              : Number(variant.cost),
          stock: Number(variant.stock) || 0,
          lowStockAlert: Number(variant.lowStockAlert) || 0,
          enabled: variant.enabled !== false,
          images: Array.isArray(variant.images) ? variant.images : [],
        }))
      : [],
    images: Array.isArray(product.images)
      ? product.images.map((image) => ({
          _id: image._id,
          path: image.path,
          name: image.name || "",
          order: image.order || 1,
          isCover: Boolean(image.isCover),
        }))
      : [],
    visible,
    featured: Boolean(product.featured),
    seo: {
      metaTitle: product.seo?.metaTitle || "",
      metaDescription: product.seo?.metaDescription || "",
    },
  };
}

export function ProductPage() {
  const { hasAction } = usePermissions();
  const canAdd = hasAction("products", ACTIONS.ADD);
  const canEdit = hasAction("products", ACTIONS.MOD);
  const canDelete = hasAction("products", ACTIONS.DEL);

  const location = useLocation();
  const actionMenuRef = useRef(null);
  const actionMenuPanelRef = useRef(null);
  const [actionMenuPosition, setActionMenuPosition] = useState(null);
  const loadRequestIdRef = useRef(0);

  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState({
    totalDocs: 0,
    totalPages: 1,
    page: 1,
    limit: 10,
  });

  const [searchValue, setSearchValue] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [isLoadingBrands, setIsLoadingBrands] = useState(true);

  const [categoryFilter, setCategoryFilter] = useState([]);
  const [brandFilter, setBrandFilter] = useState([]);

  const [limit, setLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [productToDelete, setProductToDelete] = useState(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdatingVisibility, setIsUpdatingVisibility] = useState(false);
  const [updatingProductId, setUpdatingProductId] = useState(null);

  const [successMessage, setSuccessMessage] = useState(
    location.state?.success || ""
  );
  const [error, setError] = useState("");

  const loadProducts = useCallback(async () => {
    const requestId = ++loadRequestIdRef.current;

    try {
      setIsLoading(true);
      setError("");
      setOpenActionMenu(null);

      const productController = new Product();

      const result = await productController.getProducts(
        currentPage,
        limit,
        {
          search: appliedSearch || undefined,
          category:
            categoryFilter.length > 0
              ? categoryFilter.join(",")
              : undefined,
          brand: brandFilter.length > 0 ? brandFilter.join(",") : undefined,
        }
      );

      if (requestId !== loadRequestIdRef.current) {
        return;
      }

      const response = getProductsResponse(result);

      setProducts(response.docs || []);
      setPagination({
        totalDocs: response.totalDocs || 0,
        totalPages: Math.max(response.totalPages || 1, 1),
        page: response.page || currentPage,
        limit: response.limit || limit,
      });
    } catch (errorResponse) {
      if (requestId !== loadRequestIdRef.current) {
        return;
      }

      console.error("loadProducts error:", errorResponse);

      setProducts([]);
      setPagination({
        totalDocs: 0,
        totalPages: 1,
        page: 1,
        limit,
      });

      setError(
        errorResponse?.msg ||
          "No fue posible cargar los productos. Intenta nuevamente."
      );
    } finally {
      if (requestId === loadRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [
    appliedSearch,
    currentPage,
    limit,
    categoryFilter,
    brandFilter,
  ]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    async function loadCategories() {
      try {
        setIsLoadingCategories(true);

        const categoryController = new Category();

        const result = await categoryController.getCategories(1, 100, {
          visible: true,
        });

        setCategories(getCategoriesResponse(result));
      } catch (errorResponse) {
        console.error("loadCategories error:", errorResponse);

        setCategories([]);
      } finally {
        setIsLoadingCategories(false);
      }
    }

    loadCategories();
  }, []);

  useEffect(() => {
    async function loadBrands() {
      try {
        setIsLoadingBrands(true);
        setBrands(await fetchClassificationsBySchema(SSMARCA));
      } catch (errorResponse) {
        console.error("loadBrands error:", errorResponse);

        setBrands([]);
      } finally {
        setIsLoadingBrands(false);
      }
    }

    loadBrands();
  }, []);

  useEffect(() => {
    if (!successMessage) {
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      setSuccessMessage("");
    }, 4000);

    return () => window.clearTimeout(timeout);
  }, [successMessage]);

  useEffect(() => {
    function handlePointerDown(event) {
      const clickedTrigger = actionMenuRef.current?.contains(event.target);
      const clickedPanel = actionMenuPanelRef.current?.contains(
        event.target
      );

      if (!clickedTrigger && !clickedPanel) {
        setOpenActionMenu(null);
        setActionMenuPosition(null);
      }
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setOpenActionMenu(null);
        setActionMenuPosition(null);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (openActionMenu === null) {
      return undefined;
    }

    function handleReposition() {
      setOpenActionMenu(null);
      setActionMenuPosition(null);
    }

    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);

    return () => {
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [openActionMenu]);

  const firstVisibleItem = useMemo(() => {
    if (pagination.totalDocs === 0) {
      return 0;
    }

    return (pagination.page - 1) * pagination.limit + 1;
  }, [pagination]);

  const lastVisibleItem = useMemo(() => {
    return Math.min(
      pagination.page * pagination.limit,
      pagination.totalDocs
    );
  }, [pagination]);

  function handleSearchSubmit(event) {
    event.preventDefault();
    setCurrentPage(1);
    setAppliedSearch(searchValue.trim());
  }

  function handleClearSearch() {
    setSearchValue("");
    setAppliedSearch("");
    setCurrentPage(1);
  }

  function handleLimitChange(event) {
    setLimit(Number(event.target.value));
    setCurrentPage(1);
  }

  function handleCategoryFilterChange(newValues) {
    setCategoryFilter(newValues);
    setCurrentPage(1);
  }

  function handleBrandFilterChange(newValues) {
    setBrandFilter(newValues);
    setCurrentPage(1);
  }

  const hasActiveFilters =
    categoryFilter.length > 0 || brandFilter.length > 0;

  function handleClearFilters() {
    setCategoryFilter([]);
    setBrandFilter([]);
    setCurrentPage(1);
  }

  function handleOpenDeleteModal(product) {
    setOpenActionMenu(null);
    setProductToDelete(product);
    setError("");
  }

  function handleCloseDeleteModal() {
    if (!isDeleting) {
      setProductToDelete(null);
    }
  }

  async function handleConfirmDelete() {
    if (!productToDelete) {
      return;
    }

    try {
      setIsDeleting(true);
      setError("");

      const productController = new Product();

      await productController.deleteProduct(productToDelete._id);

      setSuccessMessage("Producto eliminado correctamente.");
      setProductToDelete(null);

      if (products.length === 1 && currentPage > 1) {
        setCurrentPage((page) => page - 1);
      } else {
        await loadProducts();
      }
    } catch (errorResponse) {
      console.error("handleConfirmDelete error:", errorResponse);

      setError(
        errorResponse?.msg ||
          "No fue posible eliminar el producto. Intenta nuevamente."
      );
    } finally {
      setIsDeleting(false);
    }
  }

  async function handleChangeVisibility(product) {
    if (isUpdatingVisibility) {
      return;
    }

    try {
      setIsUpdatingVisibility(true);
      setUpdatingProductId(product._id);
      setError("");

      const productController = new Product();

      await productController.updateProduct(
        product._id,
        getProductPayload(product, !product.visible)
      );

      setSuccessMessage(
        product.visible
          ? "Producto oculto de la tienda."
          : "Producto visible en la tienda."
      );

      await loadProducts();
    } catch (errorResponse) {
      console.error("handleChangeVisibility error:", errorResponse);

      setError(
        errorResponse?.msg ||
          "No fue posible actualizar la visibilidad del producto."
      );
    } finally {
      setIsUpdatingVisibility(false);
      setUpdatingProductId(null);
    }
  }

  return (
    <section className={styles.ProductPage}>
      <div className={styles.TopBar}>
        <form className={styles.SearchForm} onSubmit={handleSearchSubmit}>
          <input
            type="search"
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            placeholder="Buscar por nombre, SKU o código..."
            aria-label="Buscar productos"
          />

          <button type="submit" aria-label="Buscar productos">
            ⌕
          </button>
        </form>

        <div className={styles.TopActions}>
          {canAdd && (
            <Link
              to="/admin/products/create"
              className={styles.CreateButton}
            >
              <span>＋</span>
              Nuevo producto
            </Link>
          )}
        </div>
      </div>

      <div className={styles.FilterBar}>
        <MultiSelectFilter
          label="Categoría"
          options={categories.map((category) => ({
            value: getEntityId(category),
            label: getCategoryOptionLabel(category),
          }))}
          selectedValues={categoryFilter}
          onChange={handleCategoryFilterChange}
          disabled={isLoadingCategories}
        />

        <MultiSelectFilter
          label="Marca"
          options={brands.map((brand) => ({
            value: getEntityId(brand),
            label: brand?.name || "",
          }))}
          selectedValues={brandFilter}
          onChange={handleBrandFilterChange}
          disabled={isLoadingBrands}
        />

        <button
          type="button"
          className={styles.ClearFiltersButton}
          onClick={handleClearFilters}
          disabled={!hasActiveFilters}
        >
          Limpiar filtros
        </button>
      </div>

      {appliedSearch && (
        <div className={styles.Filters}>
          <span className={styles.ActiveSearch}>
            Búsqueda: <strong>{appliedSearch}</strong>
          </span>

          <button
            type="button"
            className={styles.ClearButton}
            onClick={handleClearSearch}
          >
            Limpiar búsqueda
          </button>
        </div>
      )}

      {successMessage && (
        <div className={styles.SuccessMessage} role="status">
          {successMessage}
        </div>
      )}

      {error && (
        <div className={styles.ErrorMessage} role="alert">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className={styles.StateMessage}>Cargando productos...</div>
      ) : products.length === 0 ? (
        <div className={styles.EmptyState}>
          <p>
            {appliedSearch
              ? "No se encontraron productos con esa búsqueda."
              : "No hay productos para mostrar."}
          </p>

          {appliedSearch ? (
            <button
              type="button"
              className={styles.ClearButton}
              onClick={handleClearSearch}
            >
              Limpiar búsqueda
            </button>
          ) : null}
        </div>
      ) : (
        <>
          <div className={styles.TableContainer}>
            <table className={styles.Table}>
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>SKU</th>
                  <th>Categoría</th>
                  <th>Precio</th>
                  <th>Stock</th>
                  <th>Estado</th>
                  <th className={styles.ActionsColumn}>Acciones</th>
                </tr>
              </thead>

              <tbody>
                {products.map((product) => {
                  const productId = getProductId(product);
                  const productImage = getProductImage(product);
                  const normalPrice = getMinVariantPrice(product);
                  const offerPrice = getMinOfferPrice(product);
                  const stockStatus = getStockStatus(product);
                  const brandName = getBrandName(product);

                  return (
                    <tr key={productId}>
                      <td>
                        <div className={styles.ProductCell}>
                          {productImage ? (
                            <img
                              className={styles.ProductImage}
                              src={productImage}
                              alt=""
                            />
                          ) : (
                            <span className={styles.ProductImagePlaceholder}>
                              ▣
                            </span>
                          )}

                          <div className={styles.ProductInfo}>
                            <p className={styles.ProductName}>
                              {product.name}
                            </p>

                            <span className={styles.ProductMeta}>
                              {brandName || product.slug || "Sin marca"}
                            </span>
                          </div>
                        </div>
                      </td>

                      <td>
                        <span
                          className={styles.SkuCode}
                          title={getMainSku(product)}
                        >
                          {getMainSku(product)}
                        </span>
                      </td>

                      <td>
                        <span
                          className={styles.CategoryName}
                          title={getCategoryName(product.category)}
                        >
                          {getCategoryName(product.category)}
                        </span>
                      </td>

                      <td>
                        <div className={styles.PriceCell}>
                          {offerPrice !== null ? (
                            <>
                              <strong>{formatCurrency(offerPrice)}</strong>
                              <span>{formatCurrency(normalPrice)}</span>
                            </>
                          ) : (
                            <strong>{formatCurrency(normalPrice)}</strong>
                          )}
                        </div>
                      </td>

                      <td>
                        <div className={styles.StockCell}>
                          <strong>{product.stock || 0}</strong>
                          <span className={stockStatus.className}>
                            {stockStatus.label}
                          </span>
                        </div>
                      </td>

                      <td>
                        <label
                          className={styles.VisibilitySwitch}
                          title={
                            product.visible
                              ? "Ocultar de la tienda"
                              : "Mostrar en la tienda"
                          }
                        >
                          <input
                            type="checkbox"
                            checked={Boolean(product.visible)}
                            onChange={() =>
                              handleChangeVisibility(product)
                            }
                            disabled={
                              !canEdit ||
                              (isUpdatingVisibility &&
                                updatingProductId === productId)
                            }
                          />

                          <span />
                        </label>

                        <small className={styles.StateLabel}>
                          {product.visible ? "Visible" : "Oculto"}
                        </small>
                      </td>

                      <td className={styles.ActionsColumn}>
                        {(canEdit || canDelete) && (
                          <div
                            ref={
                              openActionMenu === productId
                                ? actionMenuRef
                                : null
                            }
                            className={styles.RowActions}
                          >
                            <button
                              type="button"
                              className={styles.MenuButton}
                              aria-label={`Acciones para ${product.name}`}
                              aria-expanded={openActionMenu === productId}
                              aria-haspopup="menu"
                              onClick={(event) => {
                                if (openActionMenu === productId) {
                                  setOpenActionMenu(null);
                                  setActionMenuPosition(null);
                                  return;
                                }

                                const rect =
                                  event.currentTarget.getBoundingClientRect();
                                const menuWidth = 175;
                                const viewportRight =
                                  document.documentElement.clientWidth;

                                const left = Math.min(
                                  rect.right + window.scrollX - menuWidth,
                                  viewportRight +
                                    window.scrollX -
                                    menuWidth -
                                    8
                                );

                                setActionMenuPosition({
                                  top: rect.bottom + window.scrollY + 6,
                                  left: Math.max(left, 8),
                                });
                                setOpenActionMenu(productId);
                              }}
                            >
                              ⋮
                            </button>

                            {openActionMenu === productId &&
                              actionMenuPosition &&
                              createPortal(
                                <div
                                  ref={actionMenuPanelRef}
                                  className={styles.ActionMenu}
                                  style={{
                                    position: "absolute",
                                    top: actionMenuPosition.top,
                                    left: actionMenuPosition.left,
                                  }}
                                  role="menu"
                                >
                                  {canEdit && (
                                    <Link
                                      to={`/admin/products/${productId}/edit`}
                                      role="menuitem"
                                      onClick={() => {
                                        setOpenActionMenu(null);
                                        setActionMenuPosition(null);
                                      }}
                                    >
                                      Editar producto
                                    </Link>
                                  )}

                                  {canDelete && (
                                    <button
                                      type="button"
                                      className={styles.DeleteAction}
                                      role="menuitem"
                                      onClick={() =>
                                        handleOpenDeleteModal(product)
                                      }
                                    >
                                      Eliminar producto
                                    </button>
                                  )}
                                </div>,
                                document.body
                              )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <footer className={styles.Pagination}>
            <span className={styles.TableInfo}>
              Mostrando {firstVisibleItem} a {lastVisibleItem} de{" "}
              {pagination.totalDocs} productos
            </span>

            <div className={styles.PageControls}>
              <button
                type="button"
                className={styles.PageButton}
                onClick={() =>
                  setCurrentPage((page) => Math.max(page - 1, 1))
                }
                disabled={currentPage === 1}
                aria-label="Página anterior"
              >
                ‹
              </button>

              <span className={styles.CurrentPage}>
                {pagination.page} de {pagination.totalPages}
              </span>

              <button
                type="button"
                className={styles.PageButton}
                onClick={() =>
                  setCurrentPage((page) =>
                    Math.min(page + 1, pagination.totalPages)
                  )
                }
                disabled={currentPage === pagination.totalPages}
                aria-label="Página siguiente"
              >
                ›
              </button>
            </div>

            <label className={styles.LimitControl}>
              <select value={limit} onChange={handleLimitChange}>
                {LIMIT_OPTIONS.map((optionLimit) => (
                  <option key={optionLimit} value={optionLimit}>
                    {optionLimit} por página
                  </option>
                ))}
              </select>
            </label>
          </footer>
        </>
      )}

      {productToDelete && (
        <div className={styles.DeleteModalOverlay}>
          <div
            className={styles.DeleteModal}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="delete-product-title"
            aria-describedby="delete-product-description"
          >
            <div className={styles.DeleteModalIcon}>!</div>

            <h2 id="delete-product-title">Eliminar producto</h2>

            <p id="delete-product-description">
              ¿Deseas eliminar el producto{" "}
              <strong>{productToDelete.name}</strong>?
            </p>

            <p className={styles.DeleteModalWarning}>
              El producto dejará de mostrarse en el catálogo y no estará
              disponible para nuevas ventas.
            </p>

            <div className={styles.DeleteModalActions}>
              <button
                type="button"
                className={styles.CancelDeleteButton}
                onClick={handleCloseDeleteModal}
                disabled={isDeleting}
                autoFocus
              >
                Cancelar
              </button>

              <button
                type="button"
                className={styles.ConfirmDeleteButton}
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Eliminando..." : "Eliminar producto"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}